import React, { useEffect } from "react";
import HandPhone from "../assets/images/ComingSoon2/HandPhone.png";

// Create ErrorBoundary component to catch ResizeObserver errors at the React level
class ErrorBoundary extends React.Component {
  componentDidCatch(error, errorInfo) {
    if (error.message && error.message.includes("ResizeObserver")) {
      // Prevent the error from propagating
      console.log("ResizeObserver error suppressed");
    } else {
      // Let other errors propagate normally
      throw error;
    }
  }

  render() {
    return this.props.children;
  }
}

const ComingSoonPage = () => {
  useEffect(() => {
    // Prevent ResizeObserver errors from reaching the console completely
    const disableResizeObserverLoopError = () => {
      // Block the error at the window.error event level
      const windowErrorHandler = (event) => {
        if (event.message && event.message.includes("ResizeObserver loop")) {
          event.stopImmediatePropagation();
          event.preventDefault();
          return false;
        }
        return true;
      };

      window.addEventListener("error", windowErrorHandler, true);

      // Also override the console.error method
      const originalConsoleError = console.error;
      console.error = (...args) => {
        if (
          args[0] &&
          ((typeof args[0] === "string" &&
            args[0].includes("ResizeObserver")) ||
            (args[0] instanceof Error &&
              args[0].message.includes("ResizeObserver")))
        ) {
          return;
        }
        originalConsoleError.apply(console, args);
      };

      return () => {
        window.removeEventListener("error", windowErrorHandler, true);
        console.error = originalConsoleError;
      };
    };

    // Apply the error suppression
    const cleanup = disableResizeObserverLoopError();

    // Load Kit script
    const script = document.createElement("script");
    script.src = "https://verisultconsult.kit.com/378fb6d865/index.js";
    script.async = true;
    script.setAttribute("data-uid", "378fb6d865");
    script.id = "kit-script";
    document.body.appendChild(script);

    // Use both interval and mutation observer for maximum reliability
    const moveFormInterval = setInterval(() => {
      const kitForm = document.querySelector('form[data-uid="378fb6d865"]');
      const formContainer = document.getElementById("kit-form-container");

      if (kitForm && formContainer && !formContainer.contains(kitForm)) {
        // Move the form to our container
        formContainer.appendChild(kitForm);
        console.log("Form moved to container");
        clearInterval(moveFormInterval);
      }
    }, 300);

    // Also set up a mutation observer to catch the form as soon as it's added to the DOM
    const observer = new MutationObserver((mutations) => {
      const kitForm = document.querySelector('form[data-uid="378fb6d865"]');
      const formContainer = document.getElementById("kit-form-container");

      if (kitForm && formContainer && !formContainer.contains(kitForm)) {
        formContainer.appendChild(kitForm);
        console.log("Form moved to container via mutation observer");
        observer.disconnect();
        clearInterval(moveFormInterval);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Set a timeout to clean everything up after 10 seconds
    const timeoutId = setTimeout(() => {
      clearInterval(moveFormInterval);
      observer.disconnect();
    }, 10000);

    // Add style to hide "Built with Kit" and improve form styling
    const style = document.createElement("style");
    style.id = "kit-form-fixes";
    style.textContent = `
      /* Hide the Kit branding */
      .formkit-powered-by,
      .formkit-powered-by-convertkit-container,
      a[href^="https://kit.co"],
      div:has(> a[href^="https://kit.co"]) {
        display: none !important;
      }
      
      /* Ensure the form displays with proper spacing */
      .formkit-form {
        margin: 0 !important;
        padding: 0 !important;
      }
      
      /* Make the fields display in a row */
      .formkit-fields {
        display: flex !important;
        flex-wrap: wrap !important;
        gap: 8px !important;
      }
      
      /* Enhanced responsive styles */
      @media (max-width: 640px) {
        .formkit-fields {
          flex-direction: column !important;
          width: 100% !important;
        }
        
        .formkit-field, .formkit-submit {
          width: 100% !important;
        }
      }
      
      /* Very small screens */
      @media (max-width: 375px) {
        .formkit-form {
          max-width: 100% !important;
        }
        
        .formkit-field input, .formkit-submit button {
          font-size: 14px !important;
          padding: 8px 12px !important;
        }
      }
    `;
    document.head.appendChild(style);

    // Cleanup function
    return () => {
      cleanup(); // Clean up error handlers

      const kitScript = document.getElementById("kit-script");
      if (kitScript) {
        document.body.removeChild(kitScript);
      }

      clearInterval(moveFormInterval);
      clearTimeout(timeoutId);
      observer.disconnect();

      const styleElement = document.getElementById("kit-form-fixes");
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  return (
    <ErrorBoundary>
      <div
        className="w-full min-h-screen flex items-center"
        style={{
          backgroundColor: "#FAF9FF",
          backgroundImage:
            "linear-gradient(to bottom right, rgba(231, 250, 237, 0.3), rgba(236, 253, 245, 0.3))",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background shapes */}
        <div
          className="absolute top-0 left-0 w-32 h-32 bg-green-50 rounded-full opacity-30"
          style={{
            transform: "translate(-50%, -50%)",
            zIndex: 0,
          }}
        ></div>
        <div
          className="absolute top-1/4 right-1/4 w-40 h-40 bg-green-50 rounded-full opacity-20"
          style={{ zIndex: 0 }}
        ></div>
        <div
          className="absolute bottom-0 right-0 w-64 h-64 bg-green-50 rounded-full opacity-30"
          style={{
            transform: "translate(30%, 30%)",
            zIndex: 0,
          }}
        ></div>

        <div className="container mx-auto max-w-6xl px-4 py-6 md:py-12 lg:py-16 z-10">
          <div className="flex flex-col md:flex-row items-center justify-between">
            {/* Left side content */}
            <div className="w-full md:w-1/2 md:pr-4 mb-8 md:mb-0 text-center md:text-left">
              <h1
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight"
                style={{ color: "#004D29" }}
              >
                Get notified
              </h1>
              <h1
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-4 md:mb-6"
                style={{ color: "#0A9F46" }}
              >
                when we launch.
              </h1>

              <p className="text-gray-700 text-base sm:text-lg mb-6 md:mb-10 max-w-md mx-auto md:mx-0">
                Get notified about the latest career-boosting opportunities.
              </p>

              {/* This container will hold the Kit form */}
              <div
                id="kit-form-container"
                className="w-full max-w-md mx-auto md:mx-0"
              ></div>
            </div>

            {/* Right side image */}
            <div className="w-full md:w-1/2 md:pl-4 flex justify-center">
              <div className="relative">
                <img
                  src={HandPhone}
                  alt="Mobile app mockup"
                  className="w-auto h-auto max-w-full max-h-72 sm:max-h-96 md:max-h-450 lg:max-h-580"
                  style={{
                    objectFit: "contain",
                    maxHeight: "580px",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ComingSoonPage;
